import { z } from "zod";
import { MedicalFields, MedicalKeys } from "../../enums/MedicalReportEnums";
import { IStep, IStepFields } from "../../interface/MedicalRecord";
import { flattenFields, getFieldFormName } from "./utils";



export const collectStepFields = (step: IStep): IStepFields[] => {
  const ownFields = flattenFields(step?.StepFields ?? []);

  const childFields =
    step?.InverseParentSteps?.flatMap(collectStepFields) ?? [];

  return [...ownFields, ...childFields];
}

export function getStepFieldNames(step: any, values?: any): string[] {
  return collectStepFields(step)
    ?.filter((f) => {
      if (f.Key === MedicalKeys.DateOfPregnancy) {
        return values?.Gender === "Female" && values?.IsPregnancy === "true";
      }
      return f.IsRequired;
    })
    .map(getFieldFormName);
}


function buildBaseSchema(fields: any) {
  const shape: any = {};

  for (const field of fields) {
    if (!field.Key) continue;

    shape[field.Key] = z.any().optional();
  }

  return z.object(shape);
}


function getValue(obj: any, path: string) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}
type Condition = {
  field: string;
  value?: any;
};

type Rule =
  | {
    type: "field";
    target: string;
    label: string;
    conditions: Condition[];
  }
  | {
    type: "array";
    parent: string;
    target: string;
    label: string;
    conditions: Condition[];
  };

function getFieldPath(field: any): string {
  return field.Key || `Attributes.${field.Id}`;
}

function extractRules(
  fields: any[],
  conditions: Condition[] = [],
  parentArray?: string
): Rule[] {
  const rules: Rule[] = [];


  for (const field of fields) {
    const isOption = field.Type === MedicalFields.Option;
    const isArray = (field.Type === MedicalFields.SelectWithInputs)



    if (isOption) {
      const parentCondition = conditions.at(-1);
      if (!parentCondition) continue;

      const newConditions: Condition[] = [
        ...conditions.slice(0, -1),
        {
          field: parentCondition.field,
          value: field.Key,
        },
      ];

      const children = field.InverseParentStepFields || [];

      rules.push(
        ...extractRules(children, newConditions, parentArray)
      );

      continue;
    }

    const fieldPath = getFieldPath(field);

    if (isArray && !field.IsRequired) {
      const children = field.InverseParentStepFields || [];
      rules.push(
        ...extractRules(children, conditions, fieldPath)
      );

      continue;
    }

    const nextConditions: Condition[] = [
      ...conditions,
      { field: fieldPath, value: undefined },
    ];

    if (field.IsRequired) {

      if (parentArray) {
        rules.push({
          type: "array",
          parent: parentArray,
          target: fieldPath,
          label: field.Name,
          conditions,
        });
      } else {
        rules.push({
          type: "field",
          target: fieldPath,
          label: field.Name,
          conditions,
        });
      }
    }

    const children = field.InverseParentStepFields || [];

    rules.push(
      ...extractRules(children, nextConditions, parentArray)
    );
  }

  return rules;
}
const collectRuleFields = (step: IStep): Rule[] => {
  const ownRules = extractRules(step.StepFields ?? []);
  const childRules = step.InverseParentSteps?.flatMap(collectRuleFields) ?? [];
  return [...ownRules, ...childRules];
};

const buildRules = (steps: IStep[]) => {
  return steps.flatMap((step: any) => collectRuleFields(step));
};


export const buildFinalSchema = (steps: IStep[], data: any, t: any) => {
 
  const allFields = steps.flatMap((s: any) => collectStepFields(s) || []);

  const baseSchema = buildBaseSchema(allFields);
  const rules = buildRules(steps);




  return baseSchema.superRefine((_, ctx) => {

    for (const rule of rules) {

      if (rule.type === "field") {
        const shouldApply = rule.conditions.every((c: any) => {
          if (c.value === undefined) return true;

          return (
            String(getValue(data, c.field)) === String(c.value)
          );
        });

        if (!shouldApply) continue;

        const value = getValue(data, rule.target);

        const isEmpty =
          value === undefined ||
          value === null ||
          value === "" ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: rule.target.split("."),
            message: `${rule.label}` + " " + t("required"),
          });
        }
      }


      if (rule.type === "array") {
        const arr = getValue(data, rule.parent);

        if (!Array.isArray(arr)) continue;

        arr.forEach((item, index) => {
          const shouldApply = rule.conditions.every((c: any) => {
            if (c.value === undefined) return true;

            return (
              String(getValue(item, c.field)) === String(c.value)
            );
          });

          if (!shouldApply) return;

          const value = getValue(item, rule.target);

          const isEmpty =
            value === undefined ||
            value === null ||
            value === "" ||
            (Array.isArray(value) && value.length === 0);

          if (isEmpty) {

            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [rule.parent, index, ...rule.target.split(".")],
              message: `${rule.label}` + " " + t("required"),
            });
          }
        });
      }
    }
  });
}